import React, { useMemo, useRef } from 'react'
import { FleetGrid } from '../../sso-workspace/fleet-grid'
import { Pill } from '../../sso-workspace/pill'
import { formatAgo } from '../../sso-workspace/format'
import { dateTimeText } from '../../date/local-time'
import { TableAgInfinite } from '../../table-builder/table-ag-infinite'
import { ColumnFormat, ColumnMenu, ColumnText } from '../../table-builder/table-columns'
import { Menu, MenuDetails } from '../../menu/menu'
import { IconActivityLog } from '../../icon/icon'
import { getEntries } from '../audit-api'
import { actorView, auditErrorText, categoryLabel, categoryTone, exactUTC, isSelfRecord, recordAfterSentence, viaText } from '../entry-format'
import { isView, viewText } from '../entry-story'
import './audit-log.scss'
import { ErrorReference } from '../../error/error-reference'
import { useErrorLine } from '../../error/use-error-line'

// The grid's block size (TableAgInfinite cacheBlockSize).
export const PAGE_SIZE = 100

// The grid's datasource over GET /audit/entries (the Request log's
// makeRequestRows, for entries). The API pages by keyset cursor, newest
// first; the infinite row model asks by offset, so each block's cursor is the
// `next` of the block before. The first block sends the filters and the
// window (window(), computed when the block is asked for, so "reload" reads
// up to now); every later block sends the filters and the cursor, which
// carries the window (a cursor with a different from/to is refused). The
// grid's own search box is the text search (q). The row count stays open
// (-1) until a page comes back without a `next`.
export const makeEntryRows = ({ load, isCurrent, base, window }) => {
  const cursors = new Map()
  const filters = { ...(base || {}) }
  delete filters.from
  delete filters.to
  delete filters.before
  delete filters.limit

  return async (start, end, search) => {
    const params = { ...filters }
    if (search) params.q = search
    if (start === 0) {
      Object.assign(params, window())
    } else {
      const before = cursors.get(start)
      if (!before) return { data: [], count: start }
      params.before = before
    }
    params.limit = Math.min(end - start, PAGE_SIZE)

    const page = (await load(params)) || { items: [] }
    if (!isCurrent()) return undefined

    const items = page.items || []
    if (page.next) cursors.set(start + items.length, page.next)
    return { data: items, count: page.next ? -1 : start + items.length }
  }
}

// Super admins' Table view of the audit log (spec §5: "Table layout" is
// sys-only; the page shows it only when /capabilities says table). Every
// cell reads through entry-format, so staff are "EducationXR support" and a
// moved or deleted record is plain text, as on the timeline. "details" opens
// the entry (onOpen(entry)).
export const AuditTable = ({ params, window, sys = false, onOpen }) => {
  const tableRef = useRef()
  const [error, failure, setError] = useErrorLine()
  const gridKey = JSON.stringify(params || {})

  const currentKey = useRef(gridKey)
  currentKey.current = gridKey

  const getRows = useMemo(
    () => {
      const mine = gridKey
      const fetchRows = makeEntryRows({
        base: params,
        isCurrent: () => currentKey.current === mine,
        window: window || (() => ({ ...(params.from ? { from: params.from } : {}), ...(params.to ? { to: params.to } : {}) })),
        load: getEntries
      })
      return async (start, end, search) => {
        try {
          setError(null)
          const result = await fetchRows(start, end, search)
          return result && { data: result.data, count: result.count }
        } catch (err) {
          if (currentKey.current !== mine) return undefined
          setError(auditErrorText(err, start === 0 ? "The audit log couldn't be loaded." : "Older entries couldn't be loaded."), err)
          return { data: [], count: start }
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gridKey]
  )

  const cells = useMemo(() => cellsFor(sys), [sys])
  const texts = useMemo(() => textsFor(sys), [sys])

  return (
    <div className="au-table">
      {error && <p className="text-danger small" role="alert">{error}</p>}
      {error && <ErrorReference error={failure} className="mb-2" />}
      {/* flush: the page's feed card is the table's one container. */}
      <FleetGrid title="entries" subtitle="Newest first" tableRef={tableRef} exportName="audit-log" flush>
        <TableAgInfinite
          pinFirstColumn
          key={gridKey}
          ref={tableRef}
          queryKey="tb"
          onGetRows={getRows}
          emptyState={error
            ? { Icon: IconActivityLog, title: "Couldn't load the entries", message: 'See the message above the list.' }
            : { Icon: IconActivityLog, title: 'No entries', message: 'Nothing matches these filters in this time range.' }}
          noMatchState={{ Icon: IconActivityLog, title: 'No matches', message: 'Nothing matches this search.' }}>
          <ColumnFormat field="occurredAt" header="When" render={cells.when} exportValue={texts.when} sortable={false} minWidth={130} />
          <ColumnFormat field="actor" header="Who" render={cells.who} exportValue={texts.who} sortable={false} minWidth={180} flex={1.5} />
          <ColumnFormat field="sentence" header="What" render={cells.what} exportValue={texts.what} sortable={false} minWidth={260} flex={3} />
          <ColumnFormat field="category" header="Category" render={cells.category} exportValue={texts.category} sortable={false} minWidth={130} flex={0.8} />
          <ColumnText field="app" header="App" sortable={false} minWidth={130} flex={1} />
          <ColumnFormat field="ip" header="IP" render={cells.ip} sortable={false} minWidth={150} flex={1} />
          <ColumnFormat field="requestID" header="Request ID" render={cells.requestID} sortable={false} minWidth={110} flex={0.7} />
          <ColumnMenu
            menu={r =>
              <Menu small>
                <MenuDetails text="details" pin show onClick={() => onOpen && onOpen(r)} />
              </Menu>}
          />
        </TableAgInfinite>
      </FleetGrid>
    </div>
  )
}

// The text of each column, for the cells and the CSV export alike, all from
// entry-format (staff stay "EducationXR support" in the file too).
const textsFor = sys => ({
  // the viewer's own time, with its zone (the file says which)
  when: e => (e.occurredAt ? dateTimeText(e.occurredAt) : ''),
  who: e => [actorView(e.actor, { sys }).text, viaText(e, { sys })].filter(Boolean).join(' '),
  // a view by its kind, as the timeline says it (entry-story.js viewText)
  what: e => (isView(e) ? viewText(e) : [e.sentence, isSelfRecord(e) ? '' : recordAfterSentence(e.sentence, e.record).text].filter(Boolean).join(' ')),
  category: e => categoryLabel(e.category)
})

const cellsFor = sys => ({
  when: row => (row.data ? <span title={exactUTC(row.value)}>{formatAgo(row.value)}</span> : ''),
  who: row => {
    if (!row.data) return ''
    const via = viaText(row.data, { sys })
    return <span>{actorView(row.data.actor, { sys }).text}{via ? <span className="sw-hint"> {via}</span> : null}</span>
  },
  what: row => (row.data ? <span title={row.data.action}>{textsFor(sys).what(row.data)}</span> : ''),
  category: row => (row.data && row.value ? <Pill tone={categoryTone(row.value)}>{categoryLabel(row.value)}</Pill> : ''),
  ip: row => {
    if (!row.data || !row.value) return ''
    const country = row.data.location && row.data.location.country
    return <span className="sw-mono">{row.value}{country ? <span className="sw-hint"> {country}</span> : null}</span>
  },
  requestID: row => (row.data && row.value ? <span className="sw-mono" title={row.value}>{String(row.value).slice(0, 8)}</span> : '')
})
